// src/components/wordbook/WordbookStats.tsx
import { FC } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
} from '@mui/material';
import {
    TrendingUp,
    Visibility as VisibilityIcon,
    LibraryBooks,
    Delete,
    Language,
} from '@mui/icons-material';
import { useWordbookOverviewStats, useLanguageStats } from '../../hooks/useWordbook';

export const WordbookStats: FC = () => {
    const { data: overviewStats, isLoading: overviewLoading, error: overviewError } = useWordbookOverviewStats();
    const { data: languageStats, isLoading: languageLoading, error: languageError } = useLanguageStats();

    if (overviewLoading || languageLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (overviewError || languageError) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    통계 데이터를 불러오는데 실패했습니다.
                </Alert>
            </Box>
        );
    }

    const n = (value: unknown): number => Number(value ?? 0);
    const overview = overviewStats ?? ({} as NonNullable<typeof overviewStats>);
    const languagePairs = languageStats?.languagePairs ?? [];

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                단어장 통계
            </Typography>

            {/* 전체 통계 */}
            <Grid container spacing={3} mb={3}>
                <Grid size={{xs: 12, sm: 6, md: 3}}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <TrendingUp color="primary" />
                                <Typography variant="subtitle2" color="text.secondary">
                                    전체 단어장
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="primary">
                                {n(overview.totalWordbooks).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                활성: {n(overview.activeWordbooks).toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{xs: 12, sm: 6, md: 3}}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <VisibilityIcon color="success" />
                                <Typography variant="subtitle2" color="text.secondary">
                                    노출 중
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="success.main">
                                {n(overview.visibleWordbooks).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                숨김: {n(overview.hiddenWordbooks).toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{xs: 12, sm: 6, md: 3}}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <LibraryBooks color="secondary" />
                                <Typography variant="subtitle2" color="text.secondary">
                                    라이브러리
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="secondary.main">
                                {n(overview.libraryWordbooks).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                전체 대비 {n(overview.totalWordbooks) > 0
                                ? ((n(overview.libraryWordbooks) / n(overview.totalWordbooks)) * 100).toFixed(1)
                                : 0}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{xs: 12, sm: 6, md: 3}}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Delete color="error" />
                                <Typography variant="subtitle2" color="text.secondary">
                                    삭제됨
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="error.main">
                                {n(overview.deletedWordbooks).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                전체 대비 {n(overview.totalWordbooks) > 0
                                ? ((n(overview.deletedWordbooks) / n(overview.totalWordbooks)) * 100).toFixed(1)
                                : 0}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* 단어 통계 */}
            <Grid container spacing={3} mb={3}>
                <Grid size={{xs: 12,  md: 6}}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                총 단어 수
                            </Typography>
                            <Typography variant="h3" color="primary" gutterBottom>
                                {n(overview.totalWords).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                단어장당 평균: {n(overview.averageWordsPerWordbook).toFixed(1)} 개
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{xs: 12,  md: 6}}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <Language color="primary" />
                                <Typography variant="h6">
                                    언어 쌍 통계
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                총 {n(languageStats?.totalPairs)} 개의 언어 쌍
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* 언어별 상세 통계 */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        언어 쌍별 상세 통계
                    </Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>학습 언어</TableCell>
                                <TableCell>의미 언어</TableCell>
                                <TableCell align="right">단어장 수</TableCell>
                                <TableCell align="right">총 단어 수</TableCell>
                                <TableCell align="right">라이브러리</TableCell>
                                <TableCell align="right">비율</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {languagePairs.map((pair, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Chip label={pair.headLang} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={pair.meaningLang} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight={500}>
                                            {n(pair.totalWordbooks).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">
                                            {n(pair.totalWords).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={n(pair.libraryWordbooks)}
                                            size="small"
                                            color="secondary"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" color="text.secondary">
                                            {n(overview.totalWordbooks) > 0
                                                ? ((n(pair.totalWordbooks) / n(overview.totalWordbooks)) * 100).toFixed(1)
                                                : 0}%
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Box>
    );
};
